import { ref } from 'vue';
import { defaultPackageName } from './queryapputils.js';
export function usePackageSave() {
  const isSaving = ref(false);
  const saveError = ref(null);
  
  async function checkExistingPackage(packageName) {
    const vcCommunicator = window.vcCommunicator;
    if (!vcCommunicator) return null;
    
    try {
      const existingPackages = await vcCommunicator.searchMetadata(null, "JobPackage");
      const packages = existingPackages?.result || [];
      
      const nameLower = packageName?.toLowerCase();
      return packages.find(p => p?.name?.toLowerCase() === nameLower) || null;
    } catch (error) {
      console.error('Error checking existing packages:', error);
      return null;
    }
  }
  
  async function savePackage(localPackage, metadata, sessionState, options = {}) {
    const {
      onConfirmOverwrite,
      skipDuplicateCheck = false
    } = options;
    
    const vcCommunicator = window.vcCommunicator;
    
    // Validation
    if (!vcCommunicator) {
      return {
        success: false,
        error: 'No communicator available',
        code: 'NO_COMMUNICATOR'
      };
    }
    
    const packageName = metadata?.name || defaultPackageName(localPackage);
    isSaving.value = true;
    saveError.value = null;
    
    try {
      // Check for existing package if not skipping
      if (!metadata?.id && !skipDuplicateCheck) {
        const existingPackage = await checkExistingPackage(packageName);
        
        if (existingPackage) {
          // Call the confirmation handler if provided
          if (onConfirmOverwrite) {
            const confirmed = await onConfirmOverwrite(existingPackage, packageName);
            if (!confirmed) {
              return {
                success: false,
                error: 'User cancelled overwrite',
                code: 'USER_CANCELLED',
                existingPackage
              };
            }
            // Update metadata with existing package
            metadata = existingPackage;
          } else {
            // No handler, just return error
            return {
              success: false,
              error: 'Package already exists',
              code: 'DUPLICATE_NAME',
              existingPackage
            };
          }
        }
      }
      
      // Save the package
      localPackage.accountUserId = sessionState?.userMdId;
      
      const result = await vcCommunicator.saveBulkSourceDefn(
        metadata?.id,
        packageName,
        metadata?.desc,
        localPackage
      );
      
      return {
        success: true,
        metadata: result.metadata,
        result: result,
        wasOverwritten: !!metadata?.id
      };
      
    } catch (error) {
      console.error('Failed to save package:', error);
      saveError.value = error || 'Failed to save package';
      
      return {
        success: false,
        error: error || 'Failed to save package',
        code: 'SAVE_FAILED',
        details: error
      };
    } finally {
      isSaving.value = false;
    }
  }
  
  return {
    isSaving,
    saveError,
    savePackage,
    checkExistingPackage
  };
}

// Helper function
function getDefaultPackageName(localPackage) {
  // Import or duplicate your defaultPackageName logic here
  return defaultPackageName(localPackage);
}